import {Fragment, useMemo} from 'react'
import {DayPicker, DayProps, Locale, Matcher, MonthGridProps, NavProps} from 'react-day-picker'
import {it} from 'react-day-picker/locale'
import {useTranslation} from '@/translations/i18n'
import {CalendarNav} from '@/components/commons/calendar/calendar-nav/CalendarNav'
import {MonthGrid} from '@/components/commons/calendar/month-grid/MonthGrid'
import {DayCell} from '@/components/commons/calendar/day-cell/DayCell'
import {DayButton} from '@/components/commons/calendar/day-button/DayButton'
import {Weekday} from '@/components/commons/calendar/week-day/Weekday'

interface CalendarPickerProps {
    month?: Date
    disabledDates?: Matcher | Matcher[] | undefined
    warningDates?: Matcher | Matcher[] | undefined
    onSelect: (date: Date, closeCalendarAfterSelect?: boolean) => void
    selected?: Date
    highlightAvailabilities?: boolean
    onPreviousMonth?: () => void
    onNextMonth?: () => void
    onMonthChange?: (date: Date) => void
}

export const CalendarPicker = ({
    month,
    disabledDates,
    warningDates,
    onSelect,
    onNextMonth,
    onPreviousMonth,
    onMonthChange,
    selected,
    highlightAvailabilities
}: CalendarPickerProps) => {
    const {
        i18n: {language}
    } = useTranslation()

    // Map language codes to react-day-picker locales
    const locale = useMemo(() => {
        const localeMap: Record<string, Locale> = {
            it: it
        }

        return localeMap[language] || it
    }, [language])

    // Compose custom components
    const components = useMemo(
        () => ({
            Nav: (props: NavProps) => <CalendarNav {...props} onSelect={onSelect} />,
            MonthGrid: (props: MonthGridProps) => <MonthGrid {...props} isRange={false} />,
            Day: (props: DayProps) => <DayCell {...props} warningDates={warningDates} />,
            DayButton: DayButton,
            Weekday: Weekday,
            MonthCaption: () => <Fragment />
        }),
        [warningDates]
    )

    // Custom modifiers
    const modifiers = useMemo(() => {
        return {
            highlightAvailabilities: !!highlightAvailabilities,
            warningDates: warningDates
        }
    }, [highlightAvailabilities, warningDates])

    return (
        <DayPicker
            month={month}
            locale={locale}
            required
            mode="single"
            selected={selected}
            onSelect={date => onSelect(date, true)}
            modifiers={modifiers}
            onNextClick={onNextMonth}
            onPrevClick={onPreviousMonth}
            onMonthChange={onMonthChange}
            disabled={disabledDates}
            components={components}
            showOutsideDays
            fixedWeeks
        />
    )
}
